import React, { useState, useCallback } from 'react';
import { apiFetch, API_BASE } from '../utils/api';
import { getOrderNumber } from '../utils/orderTitle';
import OrderReasonDialog from '../components/OrderReasonDialog';

/**
 * THE opened (expanded) order's shared core — one definition of the expanded
 * row's DETAIL CACHE and of every write that lands in it, plus the reason
 * dialog its destructive actions raise.
 *
 * Extracted 2026-10-03 from `hooks/useDeptOrderExpansion.js`,
 * `hooks/useMyOrderExpansion.js` and `pages/Approvals.js`, which had carried
 * the same four state hooks and the same nine handlers side by side.
 *
 * ## THE INVARIANT — read this before adding anything
 *
 * 🔑 **NOTHING IN HERE RUNS A `useEffect`, AND NOTHING IN HERE ASKS WHO THE
 * VIEWER IS.** Those are the two properties that make it safe to share, and
 * they are the ones to defend. The hook takes exactly ONE input (`setOrders`,
 * so the two derived first-item columns can be mirrored onto the collapsed
 * row).
 *
 * ⚠️ **THAT FIRST LINE READ "everything in here is a function of the DETAIL
 * CACHE and nothing else" FOR ONE COMMIT, AND WAS WIDENED DELIBERATELY** when
 * the reason dialog arrived: `check-duplication` found `requestOrderReason`
 * written identically in both expansion hooks, and the choice was between a
 * declared duplication group and admitting the prompt here. It is admitted.
 * The no-effect rule — the load-bearing one — is untouched; a dialog node is
 * not an effect, and `hooks/useUnsavedChangesGuard.js` already returns its
 * `dialog` the same way. **Do not read the widening as licence for anything
 * else: the two rules above are the test, not "is it about the open row".**
 *
 * The no-effect rule is not a tidiness preference — it is the property that
 * made the extraction safe:
 *
 * 🚨 **A SHARED HOOK THAT OWNS AN EFFECT OWNS THE REQUEST ORDER OF EVERY
 * SURFACE THAT CALLS IT.** React runs effects in declaration order, so effect
 * order IS request order, and the two parity probes
 * (`pages/__golden__/{MyOrders,DepartmentOrders}.probe.json`) record the whole
 * recorded CALL LIST, not merely its length. The 2026-10-03 predecessor of this
 * file put the reference-list effects into one shared hook with one fixed order
 * and silently reordered My Orders' seven mount requests; four
 * `MyOrders.parityGolden` states went red with
 * `- "url": "/api/productions"` / `+ "url": "/api/budget/cost-codes/active?…"`.
 * **So: no effect in this file, ever.** A new effect belongs at the call site,
 * where the order it runs in is visible. (`hooks/useOrderPickerLists.js` is the
 * same lesson solved the other way — three exports the caller sequences itself.)
 *
 * ## What is DELIBERATELY NOT here, and why
 *
 * - **`toggleExpand`** — three genuinely different bodies. Department Orders and
 *   My Orders are byte-identical to each other, but Approvals' is 2,340 B
 *   against their 1,982 B (it collapses its reject form and runs its own
 *   save-epoch). Measured, not assumed.
 * - **`handleEditorSaved`** — 982 / 378 / 1,139 B. Three different post-save
 *   contracts (the caller owns `editDirtyId` on My Orders; Approvals refetches
 *   the pending list).
 * - **`handleEditorDirtyChange`** — Department Orders and Approvals are
 *   identical; My Orders' is the odd one out because the PAGE owns `editDirtyId`
 *   there. Left alone rather than parameterised: the difference is *which
 *   component holds the state*, which is exactly what a parameter would hide.
 * - **`handleAttachmentsChanged`** — Department Orders' also reports the order
 *   mutation upward for the hires paperclip; My Orders' does not.
 * - **`handleStatusJumpSaved`** — identical on the two hooks, and NOT here on
 *   purpose: it needs `requestListRefresh` and `onOrderMutated`, which the two
 *   files compute at very different points (line ~137 on My Orders, ~180 on
 *   Department Orders). Hoisting it would drag this hook's call site — and with
 *   it the `orderDetails` declaration — across ~45 lines of unrelated hooks on
 *   one of the two surfaces, which is a risk out of all proportion to 289 bytes.
 *   Revisit only if the two surfaces' refresh wiring converges first.
 * - **Every GATE.** `useOrderAuthority().canManageOrder`, `canActionOrder`,
 *   `canApproveReject`, `canSkipApprovals`, `RECEIVE_ENABLED_STATUSES` and the
 *   approve/reject/override triggers stay on their own surfaces. Nothing in this
 *   file asks who you are; the only question it answers is "what is in the
 *   cache". 🔑 **If a future change needs a capability in here, that is the
 *   signal the change belongs at the call site instead.**
 *
 * ## Why the cache writes are hand-patches and not refetches
 *
 * Moved verbatim with `handleItemNotifyUpdated` from `useDeptOrderExpansion.js`,
 * where it was the only copy of the reasoning although three surfaces ran the
 * code:
 *
 *   🔑 The SAME hand-patch shape as `handleCostCodeChange` /
 *   `handlePaymentTypeChange`, and it is safe for the SAME reason CLAUDE.md
 *   records for those four handlers: `notify_reasons` and `notify_note` are
 *   inputs to no derived flag, so a stale `can_edit` / `can_action` cannot be
 *   produced by one. **`InlineStatusJump` is the one that rests on its gate
 *   alone; this is not that shape.**
 *
 *   🔴 Deliberately NOT `refreshOneOrder` and NOT `fetchOrders(true)`: neither
 *   `order_total`, `item_count` nor any list column moves when a line is
 *   flagged, so a refetch would be a request that changes nothing on screen —
 *   and on an order page it would also fight the 30s poll's `paused` contract
 *   for no benefit.
 *
 * ## Two divergences closed on the way in — both behaviour-neutral, declared
 *
 * The predecessor report called all seven handlers "byte-identical across all
 * three files". Measured here by extracting each body and comparing bytes, two
 * of the seven were not:
 *
 * 1. `updateOrderListFromItem` — Department Orders carried one extra COMMENT
 *    line (649 B vs 573 B). The comment is kept below.
 * 2. `handlePoke` — My Orders' copy listed `currentUser` in its `useCallback`
 *    dependency array and **never read it** (730 B vs 694 B; the rest is
 *    whitespace). The unused dependency is dropped, which only stops the
 *    callback's identity churning when the signed-in name changes; the body it
 *    runs is unchanged.
 *
 * @param {object}   params
 * @param {Function} params.setOrders — the caller's list setter. Only
 *   `updateOrderListFromItem`, `handlePoNumberSaved` and
 *   `handleExpectedDeliverySaved` use it, and only to mirror a value the
 *   collapsed row already shows.
 */
export default function useExpandedOrderCore({ setOrders }) {
  // Expandable row state. `expandedOrders` is a Set of order ids; the three
  // surfaces each own their own `toggleExpand` over it (see the docblock).
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [orderDetails, setOrderDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  const [pokeLoading, setPokeLoading] = useState({});

  // Helper to update collapsed card labels from first item's details.
  // 🪤 Deliberately a PLAIN function, not a `useCallback`: it is re-created on
  // every render in all three originals, and `handleCostCodeChange` /
  // `handlePaymentTypeChange` close over it. Memoising it here would change
  // those two handlers' identity semantics as well as its own.
  const updateOrderListFromItem = (orderId, updatedItem) => {
    setOrders(prev => prev.map(o => {
      if (o.id !== orderId) return o;
      // Update first_item fields if this is the first item (or only item)
      const detail = orderDetails[orderId];
      const items = detail?.items;
      if (items && items[0] && items[0].id === updatedItem.id) {
        return {
          ...o,
          first_item_cost_code: updatedItem.cost_code ? `${updatedItem.cost_code} – ${updatedItem.cost_code_name}` : '',
          first_item_payment_type: updatedItem.payment_type_name || '',
        };
      }
      return o;
    }));
  };

  // The one shape both accounting selects use: PATCH the line, merge the
  // server's row back into the cache, mirror the first item onto the list row.
  // 🪤 A non-ok response is SILENT by design (the select reverts on the next
  // render because the cache never moved) — three surfaces agreed on that and
  // changing it is a product decision, not a refactor.
  const patchItemField = async (orderId, itemId, path, body, failureLabel) => {
    try {
      const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/items/${itemId}/${path}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setOrderDetails(prev => {
          const updated = { ...prev };
          if (updated[orderId] && updated[orderId].items) {
            updated[orderId] = {
              ...updated[orderId],
              items: updated[orderId].items.map(item =>
                item.id === itemId ? { ...item, ...data.item } : item
              ),
            };
          }
          return updated;
        });
        updateOrderListFromItem(orderId, data.item);
      }
    } catch (err) {
      console.error(failureLabel, err);
    }
  };

  // 🪤 Both RETURN `patchItemField`'s promise rather than `await`ing it inside a
  // second `async` body. An extra `async` wrapper would resolve the caller one
  // microtask later than the originals did, which is a timing change an
  // `await handleCostCodeChange(…)` in a test can see. Returning the promise is
  // the same promise.
  const handleCostCodeChange = (orderId, itemId, costCodeId) => patchItemField(
    orderId, itemId, 'cost-code',
    { cost_code_id: costCodeId || null },
    'Failed to update cost code:'
  );

  const handlePaymentTypeChange = (orderId, itemId, paymentTypeId) => patchItemField(
    orderId, itemId, 'payment-type',
    { payment_type_id: paymentTypeId || null },
    'Failed to update payment type:'
  );

  // NOTIFY USER — patch one line back into the detail cache after the flag is
  // set or cleared. The reasoning is in the docblock above.
  const handleItemNotifyUpdated = useCallback((orderId, updatedItem) => {
    if (!updatedItem || updatedItem.id == null) return;
    setOrderDetails(prev => {
      const updated = { ...prev };
      if (updated[orderId] && updated[orderId].items) {
        updated[orderId] = {
          ...updated[orderId],
          items: updated[orderId].items.map(item =>
            item.id === updatedItem.id ? { ...item, ...updatedItem } : item
          ),
        };
      }
      return updated;
    });
  }, []);

  // `InlinePoNumberEditor`'s `onSaved` — hoisted out of the per-row map
  // callback for `OrderExpandedRow` (2026-08-16 extraction). It never
  // captured `order` from the closure even inline (it takes `orderId` as a
  // parameter), so hoisting it is a pure relocation, not a behaviour change.
  const handlePoNumberSaved = useCallback((orderId, normalised) => {
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, po_number: normalised } : o));
    setOrderDetails(prev => prev[orderId] ? { ...prev, [orderId]: { ...prev[orderId], po_number: normalised } } : prev);
  }, [setOrders]);

  // `InlineExpectedDeliveryEditor`'s `onSaved` — same shape as the PO one above.
  const handleExpectedDeliverySaved = useCallback((orderId, normalisedDate) => {
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, expected_delivery_date: normalisedDate } : o));
    setOrderDetails(prev => prev[orderId] ? { ...prev, [orderId]: { ...prev[orderId], expected_delivery_date: normalisedDate } } : prev);
  }, [setOrders]);

  // The detail-only cache write: whoever fetched a fresh detail hands it over.
  // 🪤 Detail-ONLY on purpose. Department Orders' attachments panel ALSO has to
  // tell a hires page the order changed (the hires row carries `file_count`),
  // and that wrapper stays on that surface — see `handleAttachmentsChanged`
  // there. Two different claims about the cache; do not merge them.
  const handleOrderFilesChanged = useCallback((orderId, freshDetail) => {
    if (!orderId || !freshDetail) return;
    setOrderDetails(prev => ({ ...prev, [orderId]: freshDetail }));
  }, []);

  // DROP the cached detail so the next expand refetches. 🪤 Calling
  // `handleOrderFilesChanged(orderId, null)` instead would silently do nothing
  // and leave the stale entry in place — that is the bug this closes.
  const handleOrderDetailInvalidated = useCallback((orderId) => {
    setOrderDetails(od => { const copy = { ...od }; delete copy[orderId]; return copy; });
  }, []);

  const handlePoke = useCallback(async (orderId, e) => {
    e.stopPropagation();
    if (pokeLoading[orderId]) return;
    try {
      setPokeLoading(pl => ({ ...pl, [orderId]: true }));
      const response = await apiFetch(`${API_BASE}/api/orders/${orderId}/poke`, { method: 'POST' });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send poke');
      }
      const result = await response.json();
      // The server words the confirmation for both stages ("Poke sent to
      // <approver>" / "Poke sent to <buyer(s)>"); at the ordering stage there
      // is no `approver_name` to build it from here. The `approver_name` arm
      // only covers a response without `message`.
      const sentTo = result.approver_name ? `Poke sent to ${result.approver_name}` : 'Poke sent';
      alert(`${result.message || sentTo}!`);
    } catch (err) {
      alert(err.message);
    } finally {
      setPokeLoading(pl => ({ ...pl, [orderId]: false }));
    }
  }, [pokeLoading]);

  // ── THE REJECT / CANCEL REASON DIALOG (2026-10-03) ───────────────────────
  // 🔀 Replaced the four `window.prompt` calls the two expansion hooks made,
  // and it lives HERE because both wrote this same `requestOrderReason` body
  // (`check-duplication` keys 60dc2d02 and c2e04917, which is how the copy was
  // caught the day it was made rather than a year later).
  //
  // 🚨 IT RESOLVES `null` ON DISMISS AND A STRING ON CONFIRM — exactly what
  // `window.prompt` answered — so every `if (reason === null) return;` at the
  // call sites is unchanged, and "backed out" still sends nothing.
  // ⚠️ `''` is reachable only on a CANCEL: the reject dialog's Confirm stays
  // disabled until the trimmed reason is non-empty.
  //
  // 🪤 The order's NUMBER is captured when the dialog opens, not read when it
  // closes: the list may be repolled in between, and a dialog naming a
  // different order than the one the user clicked is worse than naming none.
  const [reasonPrompt, setReasonPrompt] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const requestOrderReason = useCallback((order, kind) => new Promise((resolve) => {
    setReasonText('');
    setReasonPrompt({ kind, orderNumber: getOrderNumber(order) || '', onResolve: resolve });
  }), []);
  // `InlineStatusJump`'s door into the same dialog — it is mid-`await` in its
  // own handler and wants the reason RETURNED, which is what the above already
  // does, so this is a one-argument adapter and not a second dialog.
  const requestRejectionReason = useCallback(
    (order) => requestOrderReason(order, 'reject'),
    [requestOrderReason]
  );
  // 🪤 RENDER THIS AT PAGE LEVEL, never inside the actions menu —
  // `ConfirmDialog` is a bare `fixed inset-0` overlay with no portal and
  // `ui/Dropdown` closes on an outside `mousedown`, so a dialog mounted in the
  // panel is unmounted by the first click in it. Both hooks put it in the
  // `dialogs` node the page renders once.
  const reasonDialog = (
    <OrderReasonDialog
      open={reasonPrompt !== null}
      kind={reasonPrompt?.kind}
      orderNumber={reasonPrompt?.orderNumber}
      reason={reasonText}
      onReasonChange={setReasonText}
      onCancel={() => {
        // 🪤 Read the pending record BEFORE clearing it — clearing is what
        // unmounts the dialog.
        const pending = reasonPrompt;
        setReasonPrompt(null);
        setReasonText('');
        pending?.onResolve?.(null);
      }}
      onConfirm={(trimmed) => {
        const pending = reasonPrompt;
        setReasonPrompt(null);
        setReasonText('');
        // 🚨 `trimmed`, never `trimmed || null`. On a CANCEL an empty string is
        // a real answer — "yes, cancel it, I have nothing to add" — and the
        // branch receiving it does `reason || undefined`, exactly as the
        // prompt's own `''` did. Mapping `''` to `null` would make the caller's
        // `=== null` test read a confirmed cancel as a withdrawn one and
        // silently do nothing.
        pending?.onResolve?.(trimmed);
      }}
    />
  );

  return {
    requestOrderReason,
    requestRejectionReason,
    reasonDialog,
    expandedOrders,
    setExpandedOrders,
    orderDetails,
    setOrderDetails,
    detailsLoading,
    setDetailsLoading,
    pokeLoading,
    setPokeLoading,
    updateOrderListFromItem,
    handleCostCodeChange,
    handlePaymentTypeChange,
    handleItemNotifyUpdated,
    handlePoNumberSaved,
    handleExpectedDeliverySaved,
    handleOrderFilesChanged,
    handleOrderDetailInvalidated,
    handlePoke,
  };
}
